import React from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, FlexRow, Icon, Separator, SubHeading} from '@forecast-it/design-system';
import {ExpenseEstimateLines} from './ExpenseEstimateLines';
import {TimeEstimateLines} from './TimeEstimateLines';
import {
	ExpenseEstimateDraft,
	expenseEstimateCount,
	Option,
	TimeEstimateDraft,
	timeEstimateCount,
} from './baselineTemplateDraft';

export type EstimateRowsPatch = {timeEstimates?: TimeEstimateDraft[]; expenseEstimates?: ExpenseEstimateDraft[]};

type Props = {
	cyPrefix: string;
	/** The deliverable these rows belong to, or null at phase level. */
	deliverableKey: string | null;
	timeEstimates: TimeEstimateDraft[];
	expenseEstimates: ExpenseEstimateDraft[];
	roles: Option[];
	categories: Option[];
	heldRoles?: Readonly<Record<string, Option>>;
	heldCategories?: Readonly<Record<string, Option>>;
	currency: string;
	fieldError: (path: string) => string | undefined;
	onChange: (patch: EstimateRowsPatch, changedPath?: string) => void;
};

/** Time Estimates then Expense Estimates, each headed by its count of filled-in rows. */
export const EstimateSections = ({
	cyPrefix,
	deliverableKey,
	timeEstimates,
	expenseEstimates,
	roles,
	categories,
	heldRoles,
	heldCategories,
	currency,
	fieldError,
	onChange,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<>
			<FlexColumn gap="s">
				<FlexRow gap="s" alignItems="center">
					<Icon icon="deliverablesTimeEstimate" size="l" />
					<SubHeading size="2">
						{formatMessage(
							{id: 'baseline_templates.phase.time_estimates'},
							{count: timeEstimateCount(timeEstimates)}
						)}
					</SubHeading>
				</FlexRow>
				<TimeEstimateLines
					cyPrefix={cyPrefix}
					deliverableKey={deliverableKey}
					rows={timeEstimates}
					roles={roles}
					heldRoles={heldRoles}
					fieldError={fieldError}
					onChange={(rows, changedPath) => onChange({timeEstimates: rows}, changedPath)}
				/>
			</FlexColumn>
			<Separator />
			<FlexColumn gap="s">
				<FlexRow gap="s" alignItems="center">
					<Icon icon="expensesNew" size="l" />
					<SubHeading size="2">
						{formatMessage(
							{id: 'baseline_templates.phase.expense_estimates'},
							{count: expenseEstimateCount(expenseEstimates)}
						)}
					</SubHeading>
				</FlexRow>
				<ExpenseEstimateLines
					cyPrefix={cyPrefix}
					deliverableKey={deliverableKey}
					rows={expenseEstimates}
					categories={categories}
					heldCategories={heldCategories}
					currency={currency}
					fieldError={fieldError}
					onChange={(rows, changedPath) => onChange({expenseEstimates: rows}, changedPath)}
				/>
			</FlexColumn>
		</>
	);
};
